import React, { useEffect, useState } from "react";
import { Assistant, FloatingAIChatButton } from "./components";
import { useFeatureFlag } from "us.common/FeatureFlagContext";
import { FeatureFlags, Module } from "us.common/constants";
import { $Modal } from "../antd";
import { MinusOutlined } from "@ant-design/icons";
import { RootState } from "us.helper/types";
import { connect, ConnectedProps } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";
import { URLType, AuthorizationProfileBMDStatus } from "us.common/constants";
import { MessageStorage } from "./components/Assistant/utils/MessageStorage";
import { useAuth } from "us.helper/hooks/useAuth";
import { hasCaseSummary } from "./components/Assistant/functions";
import "./UniQ.scss";
import { Rnd } from "react-rnd";
import {
  clampModalPosition,
  getInitialModalPosition,
  getModalSizeStorageKey,
  loadStoredModalSize,
  parseStoredModalSize,
  saveStoredModalSize,
} from "./functions";
import {
  INITIAL_MODAL_HEIGHT,
  INITIAL_MODAL_WIDTH,
  MIN_MODAL_HEIGHT,
  MIN_MODAL_WIDTH,
} from "./constants";

export const UniQContext = React.createContext({ isOpen: false });

/**
 * UniQ component renders a floating AI chat button and a modal assistant interface.
 *
 * - Displays the chat button only if the `UNIQ_ASSISTANT` feature flag is enabled.
 * - Clicking the button toggles the visibility of the assistant modal.
 * - The modal contains the `Assistant` component and is styled with custom properties.
 *
 * @returns {JSX.Element} The rendered UniQ assistant UI.
 */
const UniQ: React.FC<PropsFromRedux & { onReset?: boolean }> = (
  props,
): JSX.Element => {
  const { authorizationProfile } = props;
  const { isUniQAutoOpenEnabled } = authorizationProfile ?? {};

  const { isEnabled } = useFeatureFlag(FeatureFlags.UNIQ_ASSISTANT);
  const { account } = useAuth();
  const { pathname } = useLocation();
  const currentUserId = account?.homeAccountId || account?.username;

  const [isOpen, setIsOpen] = useState(false);
  const [shouldReset, setShouldReset] = useState(false);
  const [state, setState] = useState(() => {
    const storedSize = loadStoredModalSize(currentUserId);
    const width = storedSize?.width ?? INITIAL_MODAL_WIDTH;
    const height = storedSize?.height ?? INITIAL_MODAL_HEIGHT;
    const { x, y } = getInitialModalPosition(width, height);

    return {
      width,
      height,
      x,
      y,
    };
  });

  const { params }: any = matchPath(
    {
      path: "/:type/:id?/:module?",
      end: false,
      caseSensitive: false,
    },
    pathname,
  ) ?? { params: {} };
  const { id: caseNo, type } = params ?? {};

  useEffect(() => {
    const storedMessages = MessageStorage.loadMessages(currentUserId);

    if (
      isUniQAutoOpenEnabled == AuthorizationProfileBMDStatus.YES &&
      !isOpen &&
      type == URLType.CASE &&
      !hasCaseSummary(storedMessages, caseNo)
    ) {
      setIsOpen(true);
    }
  }, [isUniQAutoOpenEnabled, type]);

  useEffect(() => {
    const storedSize = loadStoredModalSize(currentUserId);

    if (!storedSize) {
      return;
    }

    setState((prev) => ({
      ...prev,
      width: storedSize.width,
      height: storedSize.height,
      ...clampModalPosition(
        storedSize.width,
        storedSize.height,
        prev.x,
        prev.y,
      ),
    }));
  }, [currentUserId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setState((prev) => ({
      ...prev,
      ...clampModalPosition(prev.width, prev.height, prev.x, prev.y),
    }));
  }, [isOpen]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const storageKey = getModalSizeStorageKey(currentUserId);

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) {
        return;
      }

      const storedSize = parseStoredModalSize(event.newValue);

      if (!storedSize) {
        return;
      }

      setState((prev) => ({
        ...prev,
        width: storedSize.width,
        height: storedSize.height,
        ...clampModalPosition(
          storedSize.width,
          storedSize.height,
          prev.x,
          prev.y,
        ),
      }));
    };

    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [currentUserId]);

  const isCollectionModule = window.APP_MODULE === Module.COLLECTION;

  const handleCloseModal = () => {
    setIsOpen(false);
    setShouldReset(true);
    // Reset the reset flag after a short delay to allow the effect to trigger
    setTimeout(() => setShouldReset(false), 100);
  };

  return (
    <div className="uniq-assistant">
      {isEnabled && isCollectionModule && (
        <FloatingAIChatButton onClick={() => setIsOpen(!isOpen)} />
      )}

      {
        <UniQContext.Provider value={{ isOpen }}>
          <$Modal
            open={isOpen}
            onCancel={handleCloseModal}
            footer={null}
            wrapClassName="uniq-modal"
            centered={false}
            mask={false}
            width={state.width}
            getContainer={false}
            closeIcon={<MinusOutlined />}
            modalRender={(modal) => (
              <Rnd
                size={{ width: state.width, height: state.height }}
                position={{ x: state.x, y: state.y }}
                dragHandleClassName="chat-header"
                cancel=".react-resizable-handle, .ant-modal-close, .ant-modal-close-x, .ant-modal-close-icon"
                minWidth={MIN_MODAL_WIDTH}
                minHeight={MIN_MODAL_HEIGHT}
                bounds="window"
                enableResizing={{
                  top: false,
                  right: false,
                  bottom: false,
                  left: false,
                  topRight: true,
                  bottomRight: true,
                  bottomLeft: true,
                  topLeft: true,
                }}
                onDragStop={(e, d) => {
                  setState((prev) => ({
                    ...prev,
                    ...clampModalPosition(prev.width, prev.height, d.x, d.y),
                  }));
                }}
                onResizeStop={(e, direction, ref, delta, position) => {
                  const width = parseInt(ref.style.width, 10);
                  const height = parseInt(ref.style.height, 10);
                  const nextState = {
                    width,
                    height,
                    ...clampModalPosition(
                      width,
                      height,
                      position.x,
                      position.y,
                    ),
                  };

                  saveStoredModalSize(
                    { width: nextState.width, height: nextState.height },
                    currentUserId,
                  );
                  setState(nextState);
                }}
              >
                {/* Inject the actual modal node */}
                <div className="uniq-modal-frame">{modal}</div>
              </Rnd>
            )}
          >
            <Assistant onReset={shouldReset} />
          </$Modal>
        </UniQContext.Provider>
      }
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  const { AuthorizationProfile } = state;
  const { authorizationProfile } = AuthorizationProfile;
  return {
    authorizationProfile,
  };
};

const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(UniQ);
